<template>
  <div class="">
    <!-- Header and Add Button -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">List of Festivals</h2>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Festival
      </button>
    </div>
    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div class="flex bg-gray-200 rounded-lg">
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '0' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '0';
            searchData();
          "
        >
          All
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '1' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '1';
            searchData();
          "
        >
          Active
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '2' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '2';
            searchData();
          "
        >
          Inactive
        </button>
      </div>
      <!--  Search and Sort Controls -->
      <div class="flex items-center gap-2">
        <vs-select v-model="search.language" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="all" text="Language" disabled />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <vs-select v-model="search.today" @change="searchData()" class="w-28" :color="'#6366f1'">
          <vs-select-item value="none" text="Day Filter" disabled />
          <vs-select-item value="0" text="All" />
          <vs-select-item value="1" text="Today" />
        </vs-select>
        <vs-checkbox v-model="search.expandDates" @change="searchData()" color="primary"> Expand Dates </vs-checkbox>
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Festivals Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3 text-xs text-start font-semibold text-gray-500 uppercase">Image</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer w-60" @click="sortBy('appearanceDate')">
              Appearance Date
              <span v-if="sortByField === 'appearanceDate'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortBy('status')">
              Status
              <span v-if="sortByField === 'status'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in all" :key="item._id" class="border-b hover:bg-gray-100 transition">
            <!-- <td class="px-4 py-1">{{ idx + 1 + (currentPage - 1) * limit }}</td> -->
            <td class="px-4 py-1 font-medium text-gray-800">{{ idx + 1 }}</td>
            <td class="relative" :title="getImageValidationTitle(item)">
              <!-- show error icon -->
              <span v-if="hasImageValidationError(item)" class="material-icons text-red-500 top-0 right-0" style="font-size: 20px; position: absolute"
                >error</span
              >
              <img
                :src="item.image || require('@/assets/images/No-image.png')"
                :key="item._id + '-' + imageKeySeed"
                class="rounded-md object-contain"
                style="width: 90px; height: 60px"
              />
            </td>
            <td class="px-4 py-1 font-medium text-gray-800">{{ item.name }}</td>
            <td class="px-4 py-1 text-start flex">
              <span style="display: flex; flex-direction: column">
                <span> Start Date: {{ item.startDate[item.startDate.length - 1] | formatDate }} </span>
                <span> End Date: {{ item.endDate[item.endDate.length - 1] | formatDate }} </span>
              </span>
              <span style="display: flex; flex-direction: column; padding-left: 1rem" v-if="item.startDate.length > 1 && item.endDate.length > 1">
                +{{ item.startDate.length - 1 }}
                <span class="material-icons" style="cursor: pointer" @click="viewDesc(item)">remove_red_eye</span>
              </span>
            </td>
            <td
              class="px-4 py-1 text-center"
              :title="
                item.languageCount && item.missingLanguages.length > 0 ? `Missing: ${item.missingLanguages.join(', ')}` : 'All languages available'
              "
            >
              <div class="flex items-center justify-center gap-2">
                <span class="text-gray-500 text-lg">{{ item.language }}</span>
                <span
                  v-if="item.languageCount && item.missingLanguages && item.language === 'EN'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full font-semibold cursor-pointer',
                    item.languageCount - item.missingLanguages.length !== 11 ? 'bg-red-900 text-white' : 'bg-blue-900 text-white',
                  ]"
                >
                  {{ item.languageCount - item.missingLanguages.length }}/{{ item.languageCount }}
                </span>
                <span v-if="item.language === 'EN'" class="material-icons text-gray-400 cursor-pointer" style="font-size: 20px">info</span>
              </div>
            </td>
            <td class="px-4 py-1 text-center font-semibold text-gray-700">
              <span
                :class="item.status ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                class="inline-block px-3 py-1 rounded-full font-semibold"
              >
                {{ item.status ? "Active" : "Inactive" }}
              </span>
            </td>
            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editingItem(item, 'edit')"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      deleteCategoryPopup = true;
                      itemToDelete = item;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
          <tr v-if="!all || all.length === 0">
            <td colspan="12" class="text-center h-full py-6 text-gray-400">No festivals found.</td>
          </tr>
        </tbody>
      </table>
      <!-- Pagination (not impl yet.)-->
      <!-- <div class="flex flex-col md:flex-row items-center justify-between mt-3 pr-3">
        <div class="invisible">
          <span>asd</span>
        </div>
        <div class="flex justify-center items-center">
          <vs-pagination v-model="currentPage" :total="totalPages" :limit="1" @change="searchData" class="my-2" />
        </div>
        <div class="flex items-center gap-2 text-sm mt-2 md:mt-0">
          <span>Show Records:</span>
          <vs-select v-model="limit" @change="searchData" class="w-20" :color="'#6366f1'">
            <vs-select-item value="15" text="15" />
            <vs-select-item value="25" text="25" />
            <vs-select-item value="50" text="50" />
          </vs-select>
        </div>
      </div> -->
    </div>
    <!-- Add/Edit Modal -->
    <vs-popup :title="isNew ? 'Add New Festival' : clone ? 'Duplicate Festival' : 'Edit Festival'" :active.sync="defaultalert">
      <!-- language filter -->
      <div class="px-2 flex justify-between w-full">
        <div class="w-1/4" v-if="!isNew">
          <label class="block text-sm font-semibold">Language</label>
          <vs-select
            :color="'#6366f1'"
            @change="fileterusingLanguage($event, cat)"
            v-model="cat.language"
            class="w-full"
            :disabled="isNew || showLoader"
          >
            <vs-select-item value text="Choose Language" disabled />
            <vs-select-item :value="item.culture" :text="item.culture" v-for="(item, index) in languages" :key="index" />
          </vs-select>
        </div>
        <div v-if="!isNew && !clone && cat.language === 'EN'" :title="'Toggle to update existing translations'">
          <!-- toggle pill to update existing translations -->
          <label class="block text-sm font-semibold mb-2">Update Translations</label>
          <toggle-pill v-model="cat.translate" :disabled="showLoader" />
        </div>
      </div>

      <div class="px-2 flex flex-col">
        <label class="block text-sm font-semibold mb-2">Name</label>
        <vs-input v-model="cat.name" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />

        <div class="flex items-center justify-between">
          <div class="flex flex-col w-full">
            <label class="block text-sm font-semibold mb-2 w-full">Festival Id</label>
            <vs-input class="w-full" placeholder="Festival Id (Keep it autofill most cases)" v-model="cat.enum_id" />
          </div>
          <vs-checkbox class="w-full" v-model="cat.status">Status (Active / Inactive)</vs-checkbox>
        </div>

        <div class="flex-1 mb-3">
          <div class="flex justify-between items-start">
            <!-- image -->
            <label class="block text-sm font-semibold"
              >Image banner
              <span class="text-xs text-red-500">(ensure aspect ratio of 3:1, Width = 1200px, Height = 400px)</span>
            </label>
            <button
              v-if="displayImage"
              type="button"
              color="danger"
              class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
              @click="deleteImage(true)"
              :disabled="showLoader"
            >
              Clear
            </button>
          </div>
          <!-- image -->
          <div
            class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
            style="width: 100%; height: 140px; min-height: 140px"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="onDropImage"
            @click="triggerFileInput"
          >
            <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
            <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
              <span class="material-icons" style="font-size: 32px">cloud_upload</span>
              <span>Drag & Drop or Click to Upload</span>
            </div>
            <input
              type="file"
              ref="fileInput"
              class="hidden"
              @change="onImageChange"
              accept="image/jpeg,image/jpg,image/webp"
              :disabled="showLoader"
            />
          </div>
        </div>

        <div class="flex flex-col mb-3">
          <label class="vs-input--label" style="padding-bottom: 0.6rem">Appearance Date(s)</label>

          <div
            v-for="(startDate, index) in cat.startDate"
            :key="'startDate-' + index"
            style="display: flex; flex-direction: column; width: 100%; align-items: center; gap: 1rem; margin-bottom: 0.5rem"
          >
            <div style="flex-direction: row; display: flex; width: 100%; align-items: center">
              <div style="flex: 1">
                <label>Start Date (pair-{{ index + 1 }})</label>
                <datepicker v-model="cat.startDate[index]" format="dd-MM-yyyy" input-class="border" style="width: 100%" />
              </div>

              <div style="flex: 1">
                <label>End Date (pair-{{ index + 1 }})</label>
                <datepicker v-model="cat.endDate[index]" format="dd-MM-yyyy" input-class="border" style="width: 100%" />
              </div>
              <vs-button icon="delete" @click="removeCustomKey(index)" color="danger" style="border-radius: 50%; z-index: 0" />
            </div>
            <vs-button v-if="index == cat.startDate.length - 1" icon="add" @click="addCustomKey('startDate')" color="primary" style="z-index: 0">
              Add pair
            </vs-button>
          </div>

          <vs-button
            icon="add"
            @click="addCustomKey('startDate')"
            color="primary"
            style="width: 25%; z-index: 0"
            v-if="cat.startDate.length == 0 || cat.endDate.length == 0"
          >
            Add pair
          </vs-button>
        </div>

        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Article</label>

          <multiselect
            v-model="cat.article"
            placeholder="Select Article"
            label="name"
            track-by="_id"
            :options="relatedAllArticles"
            :multiple="false"
          ></multiselect>
        </vs-col>

        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Music Category</label>

          <multiselect
            v-model="cat.god"
            placeholder="Select Music Category"
            label="name"
            track-by="_id"
            :options="gods"
            :multiple="false"
          ></multiselect>
        </vs-col>

        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Bhajan</label>

          <multiselect
            v-model="cat.bhajans"
            placeholder="Select Bhajan"
            label="title"
            track-by="_id"
            :options="musicList.bhajans"
            :multiple="true"
          ></multiselect>
        </vs-col>

        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Shlokas</label>

          <multiselect
            v-model="cat.shlokas"
            placeholder="Select shlokas"
            label="title"
            track-by="_id"
            :options="musicList.shlokas"
            :multiple="true"
          ></multiselect>
        </vs-col>
        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Mantra</label>

          <multiselect
            v-model="cat.mantras"
            placeholder="Select Mantra"
            label="title"
            track-by="_id"
            :options="musicList.mantras"
            :multiple="true"
          ></multiselect>
        </vs-col>
        <vs-col vs-lg="12" vs-xs="12">
          <label>Select Daily Status Category</label>

          <multiselect
            v-model="cat.dailyStatusCategory"
            placeholder="Select Category"
            label="name"
            track-by="_id"
            :options="dailyStatus"
          ></multiselect>
        </vs-col>
        <vs-col vs-lg="12" vs-xs="12">
          <label>Related Article</label>

          <multiselect
            v-model="cat.relatedArticles"
            placeholder="Select Articles"
            label="name"
            track-by="_id"
            :options="relatedAllArticles"
            :multiple="true"
          ></multiselect>
        </vs-col>

        <vs-col vs-lg="6" vs-xs="6" class="mt-2 mb-4">
          <vs-checkbox v-model="cat.notification">Notification (Active / Inactive)</vs-checkbox>
        </vs-col>

        <div class="flex-1 mb-3" v-if="cat.notification">
          <div class="flex justify-between items-start">
            <!-- image -->
            <label class="block text-sm font-semibold">Notification Image</label>
            <button
              v-if="notificationImage"
              type="button"
              color="danger"
              class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
              @click="deleteImage(false)"
              :disabled="showLoader"
            >
              Clear
            </button>
          </div>
          <!-- image -->
          <div
            class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
            style="width: 100%; height: 140px; min-height: 140px"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="onDropImageFeature"
            @click="triggerFeatureFileInput"
          >
            <img v-if="notificationImage" :src="notificationImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
            <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
              <span class="material-icons" style="font-size: 32px">cloud_upload</span>
              <span>Drag & Drop or Click to Upload</span>
            </div>
            <input
              type="file"
              ref="featureFileInput"
              class="hidden"
              @change="onFeatureImageChange"
              accept="image/jpeg,image/jpg,image/webp"
              :disabled="showLoader"
            />
          </div>
        </div>
        <div class="flex flex-col md:flex-row gap-3">
          <vs-button :loading="showLoader" color="primary" type="filled" class="flex-1" :disabled="showLoader" @click="btnAddCategory">{{
            isNew ? (cat.translate ? "Save and Translate" : "Save") : "Update one"
          }}</vs-button>
          <!-- <vs-button
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              :disabled="showLoader"
              @click="createAndtTranslate"
              v-if="isNew"
              >Save and Translate</vs-button
            > -->
          <vs-button
            :loading="showLoader"
            color="primary"
            type="filled"
            class="flex-1"
            :disabled="showLoader"
            @click="updateAndTranslate"
            v-if="!isNew && !clone && cat.language === 'EN'"
            >Update all</vs-button
          >
          <!-- save and translate toggle -->
          <div class="flex flex-col" v-if="isNew" style="width: 50px">
            <label class="block text-xs font-semibold">Translate?</label>
            <toggle-pill v-model="cat.translate" :disabled="showLoader" />
          </div>
          <!-- <vs-checkbox v-model="cat.translate" v-if="!isNew && !clone && cat.language === 'EN'" class="mt-2"> Update translations </vs-checkbox> -->
        </div>
      </div>
    </vs-popup>
    <!-- delete modal -->
    <vs-popup title="Delete Festival" :active.sync="deleteCategoryPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this festival?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="deleteCategory">Delete</vs-button>
        <vs-button color="primary" @click="deleteCategoryPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>
    <!-- view start/endDates in detail -->
    <vs-popup title="View Start and End Dates" :active.sync="viewPopup">
      <div v-html="desc"></div>
    </vs-popup>
  </div>
</template>

<script>
import { API } from "@/common/constants";
import api from "@/rest.js";
import moment from "moment";
import Vue from "vue";
import Multiselect from "vue-multiselect";
import Datepicker from "vuejs-datepicker";
import TogglePill from "../components/Common/TogglePill.vue";

Vue.filter("formatDate", function (value) {
  if (value) {
    return moment(String(value)).format("DD-MM-YYYY");
  }
});

export default {
  name: "Festival",
  components: {
    // uploader,
    Multiselect,
    Datepicker,
    TogglePill,
  },
  data: () => ({
    title: "Festival",
    defaultalert: false,
    deleteCategoryPopup: false,
    isNew: true,
    clone: false,
    displayImage: "",
    notificationImage: "",
    fileName: "",
    notificationfileName: "",
    sortDirection: "asd",
    viewPopup: false,
    desc: "",
    sortByField: "",
    search: {
      sort: "ASC",
      language: "EN",
      status: "0",
      today: "none",
      expandDates: false,
    },
    currentPage: 1,
    totalPages: 1,
    limit: 15,
    cat: {
      translate: false,
      status: true,
      language: "",
      position: "",
      name: "",
      image: "",
      endDate: [],
      startDate: [],
      article: "",
      relatedArticles: [],
      bhajans: [],
      mantras: [],
      shlokas: [],
      god: null,
      dailyStatusCategory: [],
      notification: false,
      notificationImage: "",
      enum_id: "",
    },
    itemToDelete: "",
    itemToUpdate: "",
    all: [],
    languages: [],
    relatedAllArticles: [],
    musicList: {
      bhajans: [],
      shlokas: [],
      mantras: [],
    },
    dailyStatus: [],
    gods: [],
    showLoader: false,
    imageKeySeed: 0, // used to force image reload
  }),
  computed: {
    sortedData() {
      return [...this.all].sort((a, b) => {
        const fieldA = this.sortByField === "appearanceDate" ? new Date(a.startDate[a.startDate.length - 1]) : a[this.sortByField];
        const fieldB = this.sortByField === "appearanceDate" ? new Date(b.startDate[b.startDate.length - 1]) : b[this.sortByField];

        if (this.sortByField === "appearanceDate") {
          return this.sortDirection === "asc" ? fieldA - fieldB : fieldB - fieldA;
        }

        if (typeof fieldA === "string") {
          return this.sortDirection === "asc" ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
        }

        return this.sortDirection === "asc" ? fieldA - fieldB : fieldB - fieldA;
      });
    },
  },
  methods: {
    getImageValidationTitle(item) {
      if (!item.imageMetaData.aspectRatio && !item.imageMetaData.width && !item.imageMetaData.height)
        return "This image has no metadata available, but please ensure it meets the required dimensions. (Aspect Ratio: 3:1, Width: = 1200px, Height: = 400px)";
      const { aspectRatio, width, height } = item.imageMetaData;
      if (aspectRatio < 3 || aspectRatio > 3.1 || width > 1200 || height > 400) {
        return `Image Aspect Ratio should be 3:1 (Current: ${
          aspectRatio !== undefined ? aspectRatio.toFixed(2) : "N/A"
        }), Width should be = 1200px (Current: ${width !== undefined ? width : "N/A"}px), Height should be = 400px (Current: ${
          height !== undefined ? height : "N/A"
        })`;
      }
      return "";
    },

    hasImageValidationError(item) {
      if (!item.imageMetaData) return false;
      const { aspectRatio, width, height } = item.imageMetaData;
      return aspectRatio < 3 || aspectRatio > 3.1 || width > 1200 || height > 400;
    },

    addCustomKey() {
      this.cat.startDate.push("");
      this.cat.endDate.push("");
    },

    removeCustomKey(index) {
      this.cat.startDate.splice(index, 1);
      this.cat.endDate.splice(index, 1);
    },

    viewDesc(item) {
      this.$data.viewPopup = true;

      const formatDate = (date) => moment(date).format("DD MMM YYYY");

      const maxLength = Math.max(item.startDate.length, item.endDate.length);

      let tableRows = "";
      for (let i = 0; i < maxLength; i++) {
        const startDate = item.startDate[i] ? formatDate(item.startDate[i]) : "—";
        const endDate = item.endDate[i] ? formatDate(item.endDate[i]) : "—";

        tableRows += `<tr>
          <td style="padding: 5px; border: 1px solid #ddd;">${i + 1}</td>
      <td style="padding: 5px; border: 1px solid #ddd;">${startDate}</td>
      <td style="padding: 5px; border: 1px solid #ddd;">${endDate}</td>
    </tr>`;
      }

      this.$data.desc = `<div>
    <table style="width: 100%; border-collapse: collapse; text-align: left;">
      <thead>
        <tr>
          <th style="padding: 8px; border: 1px solid #ddd;">#</th>
          <th style="padding: 8px; border: 1px solid #ddd;">Start Date</th>
          <th style="padding: 8px; border: 1px solid #ddd;">End Date</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
  </div>`;
    },

    sortByDate(field) {
      if (this.sortByField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortByField = field;
        this.sortDirection = "asc";
      }

      this.all.sort((a, b) => {
        let dateA, dateB;

        if (field === "appearanceDate") {
          dateA = a.startDate.length ? new Date(a.startDate[a.startDate.length - 1]) : new Date(0);
          dateB = b.startDate.length ? new Date(b.startDate[b.startDate.length - 1]) : new Date(0);
        }

        return this.sortDirection === "asc" ? dateB - dateA : dateA - dateB;
      });
    },

    sortBy(field) {
      if (this.sortByField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortByField = field;
        this.sortDirection = "asc";
      }
      this.all = this.sortedData;
    },

    async addingNewItem() {
      this.$data.defaultalert = true;
      this.$data.isNew = true;
      this.$data.cat = {
        status: true,
        language: "EN",
        position: "",
        name: "",
        image: "",
        endDate: [],
        startDate: [],
        notification: false,
        notificationImage: "",
        god: null,
        enum_id: "",
      };
      this.$data.displayImage = "";
      this.$data.notificationImage = "";
      if (this.$data.search.language === "EN") {
        const festivalId = await this.getFestivlId("EN");
        if (festivalId) {
          this.$data.cat.enum_id = festivalId;
        }
      }
      if (this.$data.search.language !== "EN")
        await Promise.all([this.getRelatedArticles("EN"), this.getRelatedMusic("EN"), this.fetchAllDailyStatus("EN"), this.fetchAllGods("EN")]);
      if (this.$route.query.addNew) this.$router.replace({ query: null });
    },

    async editingItem(i, data) {
      this.$vs.loading();
      let findID = this.$data.search.expandDates ? i._origId : i._id;
      if (this.$data.search.language === "EN") {
        const festivalId = await this.getFestivlId("EN");
        if (festivalId) {
          this.$data.cat.enum_id = festivalId;
        }
      }
      if (data === "edit") this.$data.itemToUpdate = findID;
      else this.$data.itemToUpdate = "";

      await this.fileterusingLanguage(i.language, i);

      this.$data.isNew = false;
      this.$data.clone = data === "clone";
      // this.$data.cat = Object.assign({}, fetch);
      // this.$data.displayImage = fetch.image;
      // this.$data.notificationImage = fetch.notificationImage;
      this.$data.defaultalert = true;

      // api
      //   .get(`${API.FESTIVAL}/${findID}?language=${i.language} `)
      //   .then((response) => {
      //     const festival = response.data;
      //     console.log("festival", response);
      //     if (festival.dailyStatusCategory) {
      //       festival.dailyStatusCategory = festival.dailyStatusCategory.filter((x) => x);
      //     }

      //     this.$data.cat = JSON.parse(JSON.stringify(festival));
      //     this.$data.defaultalert = true;
      //     this.$data.isNew = false;
      //     this.$data.displayImage = festival.image;
      //     this.$data.notificationImage = festival.notificationImage;
      //     this.$data.clone = data === "clone";

      //     this.$vs.loading.close();
      //   })
      //   .catch((error) => {
      //     console.error(error);
      //     this.$vs.loading.close();
      //   });
    },

    idExtract(item, type) {
      if (item && item.length > 0) {
        return item.map((x) => {
          if (!x) return null;
          return x[type];
        });
      }
      return [];
    },

    guidOrId(item) {
      if (item && item.length > 0) {
        return item.map((x) => {
          if (!x) return null;
          if (x._id) return x._id;
          else return x.guid;
        });
      }
      return [];
    },

    async updateAndTranslate() {
      const filterNullFromDates = (dates) => {
        return dates.filter((x) => x != "" && x != null);
      };

      this.$data.cat.image = this.$data.displayImage;
      this.$data.cat.notificationImage = this.$data.notificationImage;

      if (!Array.isArray(this.$data.cat.dailyStatusCategory)) this.$data.cat.dailyStatusCategory = [this.$data.cat.dailyStatusCategory];

      const validator = (data) => {
        let missingFields = [];

        if (!data.language) missingFields.push("Language");
        if (!data.name) missingFields.push("Name");
        if (!data.enum_id) missingFields.push("Festival Id");
        if (!data.image) missingFields.push("Image");
        if (!data.startDate || data.startDate.length === 0 || data.startDate.filter((x) => x != "").length === 0) missingFields.push("Start Date");
        if (!data.endDate || data.endDate.length === 0 || data.endDate.filter((x) => x != "").length === 0) missingFields.push("End Date");

        if (data.startDate.length > 1 && data.startDate.filter((x) => x != "").length !== data.endDate.filter((x) => x != "").length) {
          missingFields.push("Make sure each start date has an end date");
        }

        if (missingFields.length > 0) {
          this.$vs.notify({
            text: `Please enter: ${missingFields.join(", ")}`,
            color: "danger",
            position: "top-right",
          });
          return false;
        }

        return true;
      };

      if (!validator(this.$data.cat)) {
        return;
      }

      const [relatedArt, sloks, bhajan, mantra, gods, dailyStatus] = await Promise.all([
        this.idExtract(this.$data.cat.relatedArticles, "_id"),
        this.idExtract(this.$data.cat.shlokas, "_id"),
        this.idExtract(this.$data.cat.bhajans, "_id"),
        this.idExtract(this.$data.cat.mantras, "_id"),
        this.idExtract([this.$data.cat.god], "_id"),
        this.guidOrId(this.$data.cat.dailyStatusCategory),
      ]);

      if (Array.isArray(this.$data.cat.startDate) && Array.isArray(this.$data.cat.endDate)) {
        const startDates = [];
        const endDates = [];

        await Promise.all([
          this.$data.cat.startDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "00:00:00",
              ).toISOString();
              startDates.push(formatted);
            }
          }),
          this.$data.cat.endDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "23:59:59",
              ).toISOString();
              endDates.push(formatted);
            }
          }),
        ]);

        this.$data.cat.startDate = filterNullFromDates(startDates);
        this.$data.cat.endDate = filterNullFromDates(endDates);
      }

      this.$vs.loading();

      let data = this.$data.cat;
      if (this.$data.cat && this.$data.cat.article && this.$data.cat.article._id) data["article"] = this.$data.cat.article._id;
      data["relatedArticles"] = relatedArt || [];
      data["bhajans"] = bhajan || [];
      data["mantras"] = mantra || [];
      data["shlokas"] = sloks || [];
      data["gods"] = gods || [];
      let statutes = [];
      if (Array.isArray(dailyStatus) && dailyStatus.length > 0) statutes = dailyStatus.filter((x) => x);
      data["dailyStatusCategory"] = statutes || [];

      const res = await api.put(`${API.FESTIVAL_U_AND_T}/${data._id}`, data);

      if (res.status == 200) {
        this.$vs.notify({
          text: "Saved Successfully",
          color: "success",
          position: "top-right",
        });
        this.$data.isNew = true;
        this.$data.itemToUpdate = "";
        this.$data.defaultalert = false;
        this.$data.cat = {
          status: true,
          language: "",
          position: "",
          name: "",
          image: "",
          endDate: "",
          startDate: "",
          notification: false,
          notificationImage: "",
          god: null,
        };
        await this.searchData();
        if (this.$route.query.language) {
          this.$router.push({ path: "/calendar" });
        }
        this.$data.displayImage = "";
        this.$data.notificationImage = "";
      }
    },

    async createAndtTranslate() {
      const filterNullFromDates = (dates) => {
        return dates.filter((x) => x != "" && x != null);
      };

      this.$data.cat.image = this.$data.displayImage;
      this.$data.cat.notificationImage = this.$data.notificationImage;

      if (!Array.isArray(this.$data.cat.dailyStatusCategory)) this.$data.cat.dailyStatusCategory = [this.$data.cat.dailyStatusCategory];

      const validator = (data) => {
        let missingFields = [];

        if (!data.language) missingFields.push("Language");
        if (!data.name) missingFields.push("Name");
        if (!data.enum_id) missingFields.push("Festival Id");
        if (!data.image) missingFields.push("Image");
        if (!data.startDate || data.startDate.length === 0 || data.startDate.filter((x) => x != "").length === 0) missingFields.push("Start Date");
        if (!data.endDate || data.endDate.length === 0 || data.endDate.filter((x) => x != "").length === 0) missingFields.push("End Date");

        if (data.startDate.length > 1 && data.startDate.filter((x) => x != "").length !== data.endDate.filter((x) => x != "").length) {
          missingFields.push("Make sure each start date has an end date");
        }

        if (missingFields.length > 0) {
          this.$vs.notify({
            text: `Please enter: ${missingFields.join(", ")}`,
            color: "danger",
            position: "top-right",
          });
          return false;
        }

        return true;
      };

      if (!validator(this.$data.cat)) {
        return;
      }

      const [relatedArt, sloks, bhajan, mantra, gods, dailyStatus] = await Promise.all([
        this.idExtract(this.$data.cat.relatedArticles, "_id"),
        this.idExtract(this.$data.cat.shlokas, "_id"),
        this.idExtract(this.$data.cat.bhajans, "_id"),
        this.idExtract(this.$data.cat.mantras, "_id"),
        this.idExtract([this.$data.cat.god], "_id"),
        this.guidOrId(this.$data.cat.dailyStatusCategory),
      ]);

      if (Array.isArray(this.$data.cat.startDate) && Array.isArray(this.$data.cat.endDate)) {
        const startDates = [];
        const endDates = [];

        await Promise.all([
          this.$data.cat.startDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "00:00:00",
              ).toISOString();
              startDates.push(formatted);
            }
          }),
          this.$data.cat.endDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "23:59:59",
              ).toISOString();
              endDates.push(formatted);
            }
          }),
        ]);

        this.$data.cat.startDate = filterNullFromDates(startDates);
        this.$data.cat.endDate = filterNullFromDates(endDates);
      }

      this.$vs.loading();

      let data = this.$data.cat;
      if (this.$data.cat && this.$data.cat.article && this.$data.cat.article._id) data["article"] = this.$data.cat.article._id;
      data["relatedArticles"] = relatedArt || [];
      data["bhajans"] = bhajan || [];
      data["mantras"] = mantra || [];
      data["shlokas"] = sloks || [];
      data["gods"] = gods || [];
      let statutes = [];
      if (dailyStatus.length > 0) statutes = dailyStatus.filter((x) => x);
      data["dailyStatusCategory"] = statutes || [];

      const res = await api.post(API.FESTIVAL_C_AND_T, data);

      if (res.status == 200) {
        this.$vs.notify({
          text: "Saved Successfully",
          color: "success",
          position: "top-right",
        });
        this.$data.isNew = true;
        this.$data.itemToUpdate = "";
        this.$data.defaultalert = false;
        this.$data.cat = {
          status: true,
          language: "",
          position: "",
          name: "",
          image: "",
          endDate: "",
          startDate: "",
          notification: false,
          notificationImage: "",
          god: null,
        };
        await this.searchData();
        this.$data.displayImage = "";
        this.$data.notificationImage = "";
      }
    },

    async btnAddCategory() {
      if (this.$data.cat.translate && this.$data.cat.language === "EN") {
        this.createAndtTranslate();
        return;
      }

      const filterNullFromDates = (dates) => {
        return dates.filter((x) => x != "" && x != null);
      };

      this.$data.cat.image = this.$data.displayImage;
      this.$data.cat.notificationImage = this.$data.notificationImage;

      if (!Array.isArray(this.$data.cat.dailyStatusCategory)) this.$data.cat.dailyStatusCategory = [this.$data.cat.dailyStatusCategory];

      const validator = (data) => {
        let missingFields = [];

        if (!data.language) missingFields.push("Language");
        if (!data.name) missingFields.push("Name");
        if (!data.enum_id) missingFields.push("Festival Id");
        if (!data.image) missingFields.push("Image");
        if (!data.startDate || data.startDate.length === 0 || data.startDate.filter((x) => x != "").length === 0) missingFields.push("Start Date");
        if (!data.endDate || data.endDate.length === 0 || data.endDate.filter((x) => x != "").length === 0) missingFields.push("End Date");

        if (data.startDate.length > 1 && data.startDate.filter((x) => x != "").length !== data.endDate.filter((x) => x != "").length) {
          missingFields.push("Make sure each start date has an end date");
        }

        if (missingFields.length > 0) {
          this.$vs.notify({
            text: `Please enter: ${missingFields.join(", ")}`,
            color: "danger",
            position: "top-right",
          });
          return false;
        }

        return true;
      };

      if (!validator(this.$data.cat)) {
        return;
      }
      // console.log(this.$data.cat);

      const [relatedArt, sloks, bhajan, mantra, gods, dailyStatus] = await Promise.all([
        this.idExtract(this.$data.cat.relatedArticles, "_id"),
        this.idExtract(this.$data.cat.shlokas, "_id"),
        this.idExtract(this.$data.cat.bhajans, "_id"),
        this.idExtract(this.$data.cat.mantras, "_id"),
        this.idExtract([this.$data.cat.god], "_id"),
        this.guidOrId(this.$data.cat.dailyStatusCategory),
      ]);

      // console.log(this.$data.cat);

      if (Array.isArray(this.$data.cat.startDate) && Array.isArray(this.$data.cat.endDate)) {
        const startDates = [];
        const endDates = [];

        await Promise.all([
          this.$data.cat.startDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "00:00:00",
              ).toISOString();
              startDates.push(formatted);
            }
          }),
          this.$data.cat.endDate.map(async (date) => {
            if (date) {
              const notPlain = new Date(date);
              const formatted = new Date(
                notPlain.getFullYear() + "/" + (notPlain.getMonth() + 1) + "/" + notPlain.getDate() + " " + "23:59:59",
              ).toISOString();
              endDates.push(formatted);
            }
          }),
        ]);

        this.$data.cat.startDate = filterNullFromDates(startDates);
        this.$data.cat.endDate = filterNullFromDates(endDates);
      }

      this.$vs.loading();
      let apiToCall = API.FESTIVAL;
      let data = this.$data.cat;
      if (this.$data.cat && this.$data.cat.article && this.$data.cat.article._id) data["article"] = this.$data.cat.article._id;
      data["relatedArticles"] = relatedArt || [];
      data["bhajans"] = bhajan || [];
      data["mantras"] = mantra || [];
      data["shlokas"] = sloks || [];
      data["gods"] = gods || [];
      const statutes = dailyStatus.filter((x) => x);
      data["dailyStatusCategory"] = statutes || [];

      if (!this.$data.isNew && !this.$data.clone) {
        apiToCall = API.FESTIVAL;
        data["image"] = this.$data.displayImage;
        data["notificationImage"] = this.$data.notificationImage;
        api
          .put(apiToCall + "?id=" + data._id, data)
          .then((r) => {
            this.$vs.loading.close();

            if (r.status === 200) {
              this.$vs.notify({
                text: "Saved Successfully",
                color: "success",
                position: "top-right",
              });
              this.searchData();
              if (this.$route.query.language) {
                this.$router.push({ path: "/calendar" });
              }
            }
          })
          .catch((error) => {
            console.log(error);
            this.$vs.loading.close();
          });
        this.$data.defaultalert = false;
        return;
      }
      if (!this.$data.isNew && !this.$data.clone) {
        this.$data.displayImage = "";
        this.$data.notificationImage = "";
      }
      if (this.$data.clone) {
        apiToCall = API.FESTIVAL;

        data = {
          name: this.$data.cat.name,
          position: this.$data.cat.position,
          language: this.$data.cat.language,
          status: this.$data.cat.status,
          image: this.$data.cat.image,
          startDate: this.$data.cat.startDate,
          endDate: this.$data.cat.endDate,
          article: this.$data.cat.article,
          relatedArticles: relatedArt || [],
          bhajans: bhajan || [],
          mantras: mantra || [],
          shlokas: sloks || [],
          gods: gods || [],
          notificationImage: this.$data.cat.notificationImage,
          notification: this.$data.cat.notification,
          dailyStatusCategory: this.$data.cat.dailyStatusCategory || [],
          enum_id: this.$data.cat.enum_id,
        };
      }
      api
        .post(apiToCall, data)
        .then((r) => {
          this.$vs.loading.close();
          if (r.status == 200) {
            this.$vs.notify({
              text: "Saved Successfully",
              color: "success",
              position: "top-right",
            });

            this.searchData();
            this.$data.defaultalert = false;
          }
        })
        .catch((error) => {
          this.$vs.loading.close();
          this.$vs.notify({
            text: `${
              error.response && error.response.data && error.response.data.meta && error.response.data.meta.message
                ? error.response.data.meta.message
                : "An error occurred while saving."
            }`,
            color: "danger",
            position: "top-right",
          });
        });
    },

    async fetchAllCategory() {
      this.$vs.loading();
      try {
        const res = await api.get(API.FESTIVAL, {});
        if (res.status) {
          for (const item of res.data.data) {
            if (typeof item.startDate === "string") {
              item.startDate = [item.startDate];
            } else if (item.startDate.length == 0) {
              item.startDate = [""];
            } else {
              item.startDate = item.startDate;
            }

            if (typeof item.endDate === "string") {
              item.endDate = [item.endDate];
            } else if (item.endDate.length == 0) {
              item.endDate = [""];
            } else {
              item.endDate = item.endDate;
            }
          }
          this.$data.all = res.data.data;
          // const allIds = this.$data.all.map((item) => item._id);
          this.sortByDate("appearanceDate");

          const targetId = this.$route.query.id;
          const targetItem = this.all.find((item) => item._id === targetId);
          if (targetItem) {
            this.editingItem(targetItem, "edit");
          }
        }
      } catch (error) {
      } finally {
        this.$vs.loading.close();
      }
    },

    async deleteCategory() {
      try {
        let headers = {
          "Content-Type": "application/json;charset=utf-8",
        };

        if (JSON.parse(localStorage.user).token) {
          headers["x-master-token"] = JSON.parse(localStorage.user).token;
        }

        let finalId = this.$data.itemToDelete._origId || this.$data.itemToDelete._id;
        const requestedDeleteDatePair = {
          startDate: this.$data.itemToDelete.startDate[0],
          endDate: this.$data.itemToDelete.endDate[0],
        };

        const res = await api.delete(`${API.FESTIVAL}/?id=${finalId}&fromExpanded=${this.$data.search.expandDates}`, {
          headers,
          data: requestedDeleteDatePair,
        });

        if (res.status) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });

          await this.searchData();
        }
      } catch (error) {
        console.error("Error deleting category:", error);
        this.$vs.notify({
          text: "Error deleting category",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$data.deleteCategoryPopup = false;
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async searchData() {
      this.$vs.loading();

      const page = !isNaN(this.currentPage) && this.currentPage > 0 ? this.currentPage : 1;

      try {
        await Promise.all([
          this.getRelatedArticles(this.$data.search.language),
          this.getRelatedMusic(this.$data.search.language),
          this.fetchAllDailyStatus(this.$data.search.language),
          this.fetchAllGods(this.$data.search.language),
        ]);

        const res = await api.get(API.FESTIVAL_DASHBOARD, {
          params: {
            sort: this.$data.search.sort,
            language: this.$data.search.language,
            status: this.$data.search.status,
            today: this.$data.search.today,
            expandDates: this.$data.search.expandDates,
            dashboard: true,
            // page: page,
            // limit: this.$data.limit,
          },
        });

        if (res.status) {
          // this.totalPages = Math.ceil(res.data.totalNumber / this.limit);

          for (const item of res.data.data) {
            if (typeof item.startDate === "string") {
              item.startDate = [item.startDate];
            } else if (!Array.isArray(item.startDate) || item.startDate.length === 0) {
              item.startDate = [""];
            }

            if (typeof item.endDate === "string") {
              item.endDate = [item.endDate];
            } else if (!Array.isArray(item.endDate) || item.endDate.length === 0) {
              item.endDate = [""];
            }
          }

          res.data.data.sort((a, b) => {
            const dateA = new Date(a.startDate[0]);
            const dateB = new Date(b.startDate[0]);
            return dateA - dateB;
          });

          this.$data.all = res.data.data;

          const targetId = this.$route.query.id;
          const targetItem = this.all.find((item) => item._id === targetId);
          if (targetItem) {
            this.editingItem(targetItem, "edit");
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$vs.loading.close();
      }
    },

    uploadImage(data, flag) {
      const imageType = ["image/jpeg", "image/jpg", "image/webp"];
      var isValid = false;
      isValid = imageType.indexOf(data.target.files[0].type) > -1;

      if (!isValid) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Only JPEG,JPG,WEBP Image files formats should be supported",
          color: "danger",
          position: "top-right",
        });
        this.$refs.fileInput.value = null;
        this.$data.displayImage = "";
        if (!flag) {
          this.$data.notificationImage = "";
          this.$refs.featureFileInput.value = null;
        }
        return;
      }
      if (data.target.files[0].size > 500000) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "File size should be less than 500kb",
          color: "danger",
          position: "top-right",
        });
        this.$refs.fileInput.value = null;
        this.$data.displayImage = "";
        if (!flag) {
          this.$data.notificationImage = "";
          this.$refs.featureFileInput.value = null;
        }
        return;
      }

      let formdata = new FormData();
      if (flag) {
        this.$data.fileName = data.target.files[0].name;
      } else {
        this.$data.notificationfileName = data.target.files[0].name;
      }

      formdata.append("category", this.$data.cat.name);
      formdata.append("file", data.target.files[0]);
      this.$vs.loading();
      api
        .post(API.UPLOAD_IMAGE, formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (response.data.error != null) {
            this.$vs.loading.close();
            this.$vs.notify({
              title: "Uploading Error",
              text: response.data.error.message,
              color: "danger",
              position: "top-right",
            });
          } else {
            const data = response;
            if (flag) {
              this.$data.displayImage = response.data.files[0].url;
            } else {
              this.$data.notificationImage = response.data.files[0].url;
            }

            if (response) {
              this.$vs.notify({
                text: "File Uploaded",
                color: "success",
                position: "top-right",
              });
              this.$vs.loading.close();
            } else {
              this.$vs.loading.close();
              this.$vs.notify({
                text: "File Uploading Failed",
                color: "danger",
                position: "top-right",
              });
            }
            this.$refs.fileInput.value = null;
            this.$emit("input", data);
          }
        })
        .catch(() => {});
      this.$vs.loading.close();
      // }
    },

    deleteImage(flag) {
      if (flag) {
        this.displayImage = "";
        this.cat.image = "";
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      } else {
        this.$data.notificationImage = "";
        if (this.$refs.featureFileInput) {
          this.$refs.featureFileInput.value = "";
        }
      }
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const ratio = width / height;

            // reject images that width > 1200px or less than 1200px
            if (width > 1210 || width < 1190) {
              this.$vs.notify({
                title: "Uploading Error",
                text: "Image width should not exceed 1200px.",
                color: "danger",
                position: "top-right",
              });
              if (this.$refs.fileInput) this.$refs.fileInput.value = null;
              this.displayImage = "";
              this.cat.image = "";
              return;
            }

            if (height > 410 || height < 390) {
              this.$vs.notify({
                title: "Uploading Error",
                text: "Image height should be approximately 400px.",
                color: "danger",
                position: "top-right",
              });
              if (this.$refs.fileInput) this.$refs.fileInput.value = null;
              this.displayImage = "";
              this.cat.image = "";
              return;
            }

            if (ratio < 3 || ratio > 4) {
              this.$vs.notify({
                title: "Uploading Error",
                text: "Image ratio (width/height) must be between 3 and 4. Ideal size: 1200x400.",
                color: "danger",
                position: "top-right",
              });
              if (this.$refs.fileInput) this.$refs.fileInput.value = null;
              this.displayImage = "";
              this.cat.image = "";
              return;
            }

            this.uploadImage(event, true);
            this.cat.image = file;
            this.displayImage = URL.createObjectURL(file);
          };
          img.onerror = () => {
            this.$vs.notify({
              title: "Uploading Error",
              text: "Invalid image file.",
              color: "danger",
              position: "top-right",
            });
            if (this.$refs.fileInput) this.$refs.fileInput.value = null;
            this.displayImage = "";
            this.cat.image = "";
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    onFeatureImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const ratio = width / height;
            if (width > 1200) {
              this.$vs.notify({
                title: "Uploading Error",
                text: "Image width should not exceed 1200px.",
                color: "danger",
                position: "top-right",
              });
              if (this.$refs.featureFileInput) this.$refs.featureFileInput.value = null;
              this.displayImageFeature = "";
              this.cat.notificationImage = "";
              return;
            }
            if (ratio < 3 || ratio > 4) {
              this.$vs.notify({
                title: "Uploading Error",
                text: "Image ratio (width/height) must be between 3 and 4. Ideal size: 1200x400.",
                color: "danger",
                position: "top-right",
              });
              if (this.$refs.featureFileInput) this.$refs.featureFileInput.value = null;
              this.displayImageFeature = "";
              this.cat.notificationImage = "";
              return;
            }
            this.uploadImage(event, false);
            this.cat.notificationImage = file;
            this.displayImageFeature = URL.createObjectURL(file);
          };
          img.onerror = () => {
            this.$vs.notify({
              title: "Uploading Error",
              text: "Invalid image file.",
              color: "danger",
              position: "top-right",
            });
            if (this.$refs.featureFileInput) this.$refs.featureFileInput.value = null;
            this.displayImageFeature = "";
            this.cat.notificationImage = "";
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.displayImageFeature = "";
      }
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    triggerFeatureFileInput() {
      if (this.$refs.featureFileInput) this.$refs.featureFileInput.click();
    },

    onDropImage(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onImageChange({ target: { files } });
      }
    },

    onDropImageFeature(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onFeatureImageChange({ target: { files } });
      }
    },

    resetData() {
      this.$data.search = {
        sort: "ASC",
        language: "EN",
        status: "0",
        today: "0",
        expandDates: false,
      };
      this.imageKeySeed++;
      this.searchData();
    },

    async fileterusingLanguage(event, item) {
      try {
        if (!event || !item) return;
        await Promise.all([this.getRelatedArticles(event), this.getRelatedMusic(event), this.fetchAllDailyStatus(event), this.fetchAllGods(event)]);
        let id = item.ref || item._id;
        this.$vs.loading();
        const res = await api.get(`${API.FESTIVAL}/${id}?language=${event}`);

        if (res.status === 200) {
          if (typeof res.data.data.startDate === "string") {
            res.data.data.startDate = [res.data.data.startDate];
          } else if (res.data.data.startDate.length == 0) {
            res.data.data.startDate = [""];
          } else {
            res.data.data.startDate = res.data.data.startDate;
          }

          if (typeof res.data.data.endDate === "string") {
            res.data.data.endDate = [res.data.data.endDate];
          } else if (res.data.data.endDate.length == 0) {
            res.data.data.endDate = [""];
          } else {
            res.data.data.endDate = res.data.data.endDate;
          }
          // console.log(res.data);
          this.$data.cat = Object.assign({}, res.data.data);
          this.$data.displayImage = res.data.data.image;
          this.$data.notificationImage = res.data.data.notificationImage;
          this.$vs.loading.close();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$vs.loading.close();
      }
    },

    copyGuid(guid) {
      var input_temp = document.createElement("input");
      input_temp.value = guid;
      document.body.appendChild(input_temp);
      input_temp.select();
      document.execCommand("copy");
      document.body.removeChild(input_temp);
      this.$vs.notify({
        text: "GUID copied",
        color: "success",
        position: "top-right",
      });
    },

    async getFestivlId(language) {
      try {
        this.$vs.loading();
        const res = await api.get(API.FESTIVAL_ID, {
          params: {
            language: language,
          },
        });
        if (res.status === 200) {
          console.log("Fetched Festival ID:", res.data.enum_id);
          return res.data.enum_id;
        }
      } catch (error) {
        console.error("Error fetching festival ID:", error);
        this.$vs.notify({
          text: "Error fetching festival ID",
          color: "danger",
          position: "top-right",
        });
        return null;
      } finally {
        this.$vs.loading.close();
      }
    },

    async getRelatedArticles(language = null) {
      let headers = {
        "Content-Type": "application/json;charset=utf-8",
      };
      if (JSON.parse(localStorage.user).token) {
        headers["token"] = this.$data.token;
      }
      this.$vs.loading();
      try {
        const response = await api.get(`${API.ARTICLE}/plain?language=EN&status=1`, {
          headers,
          params: {
            language: language,
          },
        });
        this.$data.relatedAllArticles = response.data.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.$vs.loading.close();
      }
    },
    getRelatedMusic(language = null) {
      let headers = {
        "Content-Type": "application/json;charset=utf-8",
      };
      if (JSON.parse(localStorage.user).token) {
        headers["token"] = this.$data.token;
      }
      this.$vs.loading();
      api
        .get(API.GET_MUSIC + "/related", {
          headers,
          params: {
            language: language,
          },
        })
        .then((r) => {
          this.$vs.loading.close();
          this.$data.musicList = r.data;
        })
        .catch(() => {});
    },
    async fetchAllDailyStatus() {
      this.$vs.loading();

      try {
        const res = await api.get(API.GET_CAT + `/plain?language=${this.$data.search.language}&sort=ASC&status=1`);

        if (res.status === 200) {
          this.$data.dailyStatus = res.data;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.$vs.loading.close();
      }
    },
    // viewAll(i) {
    //   // console.log(i._id);
    //   // this.$router.push("/articles/" + i._id);
    // },
    // async updateStatus(id, data) {
    //   this.$vs.loading();
    //   try {
    //     const res = await api.put(API.FESTIVAL + "/disable" + "?id=" + id, { status: data });
    //     if (res.status == 200) {
    //       this.$vs.notify({
    //         text: "Status Updated",
    //         color: "success",
    //         position: "top-right",
    //       });
    //       await this.searchData();
    //       this.$vs.loading.close();
    //     }
    //   } catch (error) {
    //     console.error(error);
    //   }
    // },
    async fetchAllGods(event) {
      this.$vs.loading();
      try {
        const res = await api.get(API.GET_GODS_DASH, {
          params: {
            position: this.$data.search.sort,
            language: event ? event : this.$data.search.language,
            plain: true,
          },
        });

        if (res.status) {
          this.$data.gods = res.data;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.$vs.loading.close();
      }
    },
    toggleExpandDates() {
      if (this.$data.search.expandDates) {
        // Expand the list by duplicating festivals for each appearance date
        const expandedList = [];
        this.$data.all.forEach((festival) => {
          if (Array.isArray(festival.startDate)) {
            festival.startDate.forEach((date, index) => {
              const clonedFestival = { ...festival };
              clonedFestival.startDate = [date];
              clonedFestival.endDate = [festival.endDate[index] || ""];
              expandedList.push(clonedFestival);
            });
          } else {
            expandedList.push(festival);
          }
        });
        this.$data.all = expandedList;
      } else {
        // Fetch the original list when expandDates is turned off
        this.searchData();
      }
    },
  },

  async created() {
    this.$data.search.language = this.$route.query.language || "EN";
    await Promise.all([this.searchData(), this.fetchAllLanguages()]);

    if (!this.$data.search.language) this.$data.search.language = "EN";

    if (this.$route.query.language) {
      this.$data.search.language = this.$route.query.language;
      this.searchData();
    }

    if (this.$route.query.addNew === "true") this.addingNewItem();
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
